/**
 * Deep vendor-profile sections (brand media, service area, booking config,
 * contact & socials) — backed by the consolidated /vendor/profile endpoints.
 * Split out of profile-page.tsx to keep that file focused on the onboarding-
 * reused business-details + location cards.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { ImageIcon, Loader2, Upload, UserRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TextField } from '@/components/text-field';
import { AuthImage } from '@/components/auth-image';
import { ApiError } from '@/lib/api';
import { form_error_message } from '@/lib/forms';
import {
  PET_SPECIES,
  type PetSpecies,
  type VendorImageKind,
} from '@/features/vendor-profile/api';
import {
  use_vendor_profile,
  use_update_vendor_profile,
  use_set_service_area,
  use_upload_vendor_image,
} from '@/features/vendor-profile/hooks';

function SavedTag({ show, testid }: { show: boolean; testid: string }) {
  if (!show) return null;
  return (
    <span className="text-sm font-medium text-primary" data-testid={testid}>
      Saved
    </span>
  );
}

// ---------------------------------------------------------------------------
// Brand & media (logo + cover + the walker/trainer face photo)
// ---------------------------------------------------------------------------

/** Only these two are a person the customer is handing their dog to. */
const MODULES_WITH_FACE_PHOTO = ['WALKER', 'TRAINER'];

export function BrandMediaCard() {
  const { data: p } = use_vendor_profile();
  const upload = use_upload_vendor_image();

  /*
   * A walker or trainer could set their face photo during onboarding and never
   * change it again: the endpoint existed, nothing here called it. Hidden for
   * every other module, which has no such photo at all — a vet's picture is
   * their logo, and offering an empty second slot only invites confusion.
   */
  const needs_face = (p?.module_types ?? []).some((m) => MODULES_WITH_FACE_PHOTO.includes(m));

  function pick(kind: VendorImageKind, input: HTMLInputElement) {
    const f = input.files?.[0];
    if (f) upload.mutate({ kind, file: f });
    input.value = '';
  }

  return (
    <Card className="lg:col-span-2">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Brand &amp; media</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-end">
        {/* Cover */}
        <div className="flex flex-1 flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Cover
          </p>
          <div className="flex h-28 items-center justify-center overflow-hidden rounded-card border-2 border-brand-outline-variant/20 bg-secondary">
            {p?.cover_url ? (
              <AuthImage
                path={p.cover_url}
                alt="Cover"
                className="h-full w-full object-cover"
                data-testid="vendor-cover-img"
              />
            ) : (
              <ImageIcon className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
          <label className="w-fit">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              data-testid="vendor-cover-input"
              onChange={(e) => pick('cover', e.target)}
            />
            <Button asChild variant="secondary" size="sm" className="gap-2">
              <span data-testid="vendor-cover-upload">
                <Upload className="h-4 w-4" /> {p?.cover_url ? 'Change cover' : 'Add cover'}
              </span>
            </Button>
          </label>
        </div>
        {/* Logo */}
        <div className="flex flex-col items-center gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Logo
          </p>
          <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-2 border-brand-outline-variant/20 bg-secondary">
            {p?.logo_url ? (
              <AuthImage
                path={p.logo_url}
                alt="Logo"
                className="h-full w-full object-cover"
                data-testid="vendor-logo-img"
              />
            ) : (
              <ImageIcon className="h-7 w-7 text-muted-foreground" />
            )}
          </div>
          <label className="w-fit">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              data-testid="vendor-logo-input"
              onChange={(e) => pick('logo', e.target)}
            />
            <Button asChild variant="secondary" size="sm" className="gap-2">
              <span data-testid="vendor-logo-upload">
                {upload.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}{' '}
                {p?.logo_url ? 'Change' : 'Add'}
              </span>
            </Button>
          </label>
        </div>
        {/* Your photo — walker and trainer only */}
        {needs_face && (
          <div className="flex flex-col items-center gap-2" data-testid="vendor-face-photo">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Your photo
            </p>
            <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-2 border-brand-outline-variant/20 bg-secondary">
              {p?.profile_photo ? (
                <AuthImage
                  path={p.profile_photo.file_path}
                  alt="Your photo"
                  className="h-full w-full object-cover"
                  data-testid="vendor-face-img"
                />
              ) : (
                <UserRound className="h-7 w-7 text-muted-foreground" />
              )}
            </div>
            <label className="w-fit">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                data-testid="vendor-face-input"
                onChange={(e) => pick('photo', e.target)}
              />
              <Button asChild variant="secondary" size="sm" className="gap-2">
                <span data-testid="vendor-face-upload">
                  <Upload className="h-4 w-4" /> {p?.profile_photo ? 'Change' : 'Add'}
                </span>
              </Button>
            </label>
            {/* Says who it is for, because it is the one picture a customer
                matches against the person at their door. */}
            <p className="max-w-[9rem] text-center text-xs text-muted-foreground">
              Customers see this when they book you.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Service area (per-module travel radius + species)
// ---------------------------------------------------------------------------

/** "CREMATION" is not what a vendor calls their own business. */
function label_for(module_type: string): string {
  const nice = module_type.toLowerCase().replace(/_/g, ' ');
  return nice.charAt(0).toUpperCase() + nice.slice(1);
}

export function ServiceAreaCard() {
  const { data: p } = use_vendor_profile();
  const update = use_update_vendor_profile();
  const set_area = use_set_service_area();
  const [species, set_species] = useState<PetSpecies[]>([]);
  const [saved, set_saved] = useState(false);

  useEffect(() => {
    if (!p) return;
    set_species(p.services_species);
  }, [p]);

  // The radius is no longer part of this form's dirty state: each module's
  // radius saves on change, because it is a choice from a fixed list rather
  // than free text and there is nothing to "finish typing".
  const dirty = !!p && species.slice().sort().join() !== p.services_species.slice().sort().join();

  function toggle(s: PetSpecies) {
    set_saved(false);
    set_species((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    set_saved(false);
    update.mutate({ services_species: species }, { onSuccess: () => set_saved(true) });
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-lg">Service area</CardTitle>
        <SavedTag show={saved && !dirty} testid="service-area-saved" />
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          {/*
            One row per module that HAS a service area. The options differ per
            module — a dog walker travelling 1km and a cremation service
            travelling 50km are not the same question — so the choices come from
            the server rather than being hardcoded as one range for everyone.
          */}
          {(p?.service_area?.modules ?? []).map((m) => (
            <div key={m.module_type} className="flex flex-col gap-1.5" data-testid={`service-area-${m.module_type}`}>
              <label className="text-sm font-medium" htmlFor={`radius-${m.module_type}`}>
                {label_for(m.module_type)} — how far you travel
              </label>
              <select
                id={`radius-${m.module_type}`}
                className="clay-select"
                value={m.radius_km ?? ''}
                disabled={set_area.isPending}
                data-testid={`service-radius-${m.module_type}`}
                onChange={(e) =>
                  set_area.mutate({ module_type: m.module_type, radius_km: Number(e.target.value) })
                }
              >
                {!m.is_declared && <option value="">Not set</option>}
                {m.options.map((km) => (
                  <option key={km} value={km}>
                    {km} km
                  </option>
                ))}
              </select>
              {/* Never leave the working number a mystery. */}
              {!m.is_declared && (
                <p className="text-xs text-warning" data-testid={`service-radius-default-${m.module_type}`}>
                  Not set — we currently show you to customers within{' '}
                  {p?.service_area?.default_radius_km ?? 10} km. Pick a distance to control this.
                </p>
              )}
              {m.is_declared && !m.filters_discovery && (
                <p className="text-xs text-muted-foreground">
                  Recorded for your profile. Customers travel to you, so this doesn’t limit who sees you.
                </p>
              )}
              {m.is_declared && m.filters_discovery && (
                <p className="text-xs text-muted-foreground">
                  Customers within {m.radius_km} km can find and book you.
                </p>
              )}
            </div>
          ))}
          {p?.service_area?.modules.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="service-area-none">
              Your services don’t use a travel radius — customers come to you.
            </p>
          )}
          <div>
            <p className="mb-1.5 text-sm font-medium">Species served</p>
            <div className="flex flex-wrap gap-2">
              {PET_SPECIES.map((s) => {
                const on = species.includes(s);
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={() => toggle(s)}
                    data-testid={`species-${s}`}
                    aria-pressed={on}
                    className={
                      'rounded-full border-2 px-3 py-1 text-sm font-semibold transition-colors duration-fast ' +
                      (on
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-brand-outline-variant/25 text-muted-foreground hover:bg-secondary')
                    }
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
          <Button
            type="submit"
            variant="gradient"
            className="w-fit"
            disabled={!dirty || update.isPending}
            data-testid="service-area-save"
          >
            {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Booking config
// ---------------------------------------------------------------------------

export function BookingConfigCard() {
  const { data: p } = use_vendor_profile();
  const update = use_update_vendor_profile();
  const [notice, set_notice] = useState('2');
  const [horizon, set_horizon] = useState('2');
  const [reschedule_notice, set_reschedule_notice] = useState('1');
  const [is_24x7, set_24x7] = useState(false);
  const [saved, set_saved] = useState(false);

  useEffect(() => {
    if (!p) return;
    set_notice(String(p.booking_min_notice_hours));
    set_horizon(String(p.booking_horizon_days));
    set_reschedule_notice(String(p.reschedule_min_notice_hours));
    set_24x7(p.is_24x7);
  }, [p]);

  const dirty =
    !!p &&
    (Number(notice) !== p.booking_min_notice_hours ||
      Number(horizon) !== p.booking_horizon_days ||
      Number(reschedule_notice) !== p.reschedule_min_notice_hours ||
      is_24x7 !== p.is_24x7);

  function submit(e: FormEvent) {
    e.preventDefault();
    set_saved(false);
    update.mutate(
      {
        booking_min_notice_hours: Number(notice),
        booking_horizon_days: Number(horizon),
        reschedule_min_notice_hours: Number(reschedule_notice),
        is_24x7,
      },
      { onSuccess: () => set_saved(true) },
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-lg">Booking config</CardTitle>
        <SavedTag show={saved && !dirty} testid="booking-config-saved" />
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Min notice (hours)"
              name="booking_min_notice_hours"
              type="number"
              min={0}
              max={168}
              value={notice}
              onChange={(e) => {
                set_notice(e.target.value);
                set_saved(false);
              }}
              data-testid="booking-notice"
            />
            <TextField
              label="Booking horizon (days)"
              name="booking_horizon_days"
              type="number"
              min={0}
              max={30}
              value={horizon}
              onChange={(e) => {
                set_horizon(e.target.value);
                set_saved(false);
              }}
              data-testid="booking-horizon"
            />
            <TextField
              label="Reschedule cutoff (hours before slot)"
              name="reschedule_min_notice_hours"
              type="number"
              min={0}
              max={168}
              value={reschedule_notice}
              onChange={(e) => {
                set_reschedule_notice(e.target.value);
                set_saved(false);
              }}
              hint="Customers cannot move a booking closer to the slot than this. You can always reschedule."
              data-testid="booking-reschedule-notice"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-input accent-primary"
              checked={is_24x7}
              onChange={(e) => {
                set_24x7(e.target.checked);
                set_saved(false);
              }}
              data-testid="booking-24x7"
            />
            We operate 24×7
          </label>
          <Button
            type="submit"
            variant="gradient"
            className="w-fit"
            disabled={!dirty || update.isPending}
            data-testid="booking-config-save"
          >
            {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Delivery time (food / pharmacy) — client change, 9 Oct 2026
// ---------------------------------------------------------------------------

/** The expected-delivery window customers see in their cart. Blank both = platform default. */
export function DeliveryTimeCard() {
  const { data: p } = use_vendor_profile();
  const update = use_update_vendor_profile();
  const [min, set_min] = useState('');
  const [max, set_max] = useState('');
  const [error, set_error] = useState<string | null>(null);
  const [saved, set_saved] = useState(false);

  useEffect(() => {
    if (!p) return;
    set_min(p.delivery_eta_min_minutes != null ? String(p.delivery_eta_min_minutes) : '');
    set_max(p.delivery_eta_max_minutes != null ? String(p.delivery_eta_max_minutes) : '');
  }, [p]);

  function submit(e: FormEvent) {
    e.preventDefault();
    set_error(null);
    set_saved(false);
    const blank = min.trim() === '' && max.trim() === '';
    const lo = Number(min);
    const hi = Number(max);
    if (!blank && (!Number.isInteger(lo) || !Number.isInteger(hi) || lo < 5 || hi < lo)) {
      set_error('Enter whole minutes, at least 5, with "from" no more than "to" — or leave both blank.');
      return;
    }
    update.mutate(
      { delivery_eta_min_minutes: blank ? null : lo, delivery_eta_max_minutes: blank ? null : hi },
      { onSuccess: () => set_saved(true), onError: (err) => set_error(form_error_message(err, 'Could not save.')) },
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-lg">Delivery time</CardTitle>
        <SavedTag show={saved} testid="delivery-eta-saved" />
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Shown to customers in their cart as the expected delivery time. Leave blank to use the standard estimate.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="From (minutes)" name="delivery_eta_min_minutes" type="number" min={5} value={min} onChange={(e) => set_min(e.target.value)} data-testid="delivery-eta-min" />
            <TextField label="To (minutes)" name="delivery_eta_max_minutes" type="number" min={5} value={max} onChange={(e) => set_max(e.target.value)} data-testid="delivery-eta-max" />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" variant="gradient" className="w-fit" disabled={update.isPending} data-testid="delivery-eta-save">
            {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Contact & socials
// ---------------------------------------------------------------------------

export function ContactSocialsCard() {
  const { data: p } = use_vendor_profile();
  const update = use_update_vendor_profile();
  const [phone, set_phone] = useState('');
  const [instagram, set_instagram] = useState('');
  const [facebook, set_facebook] = useState('');
  const [error, set_error] = useState<string>();
  const [saved, set_saved] = useState(false);

  useEffect(() => {
    if (!p) return;
    set_phone(p.business_phone ?? '');
    set_instagram(p.instagram_url ?? '');
    set_facebook(p.facebook_url ?? '');
  }, [p]);

  const dirty =
    !!p &&
    (phone !== (p.business_phone ?? '') ||
      instagram !== (p.instagram_url ?? '') ||
      facebook !== (p.facebook_url ?? ''));

  function submit(e: FormEvent) {
    e.preventDefault();
    set_error(undefined);
    set_saved(false);
    update.mutate(
      { business_phone: phone, instagram_url: instagram, facebook_url: facebook },
      {
        onSuccess: () => set_saved(true),
        onError: (err) =>
          set_error(
            err instanceof ApiError ? form_error_message(err, 'Could not save') : 'Could not save',
          ),
      },
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-lg">Contact &amp; socials</CardTitle>
        <SavedTag show={saved && !dirty} testid="contact-socials-saved" />
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          {error && <p className="text-sm text-destructive">{error}</p>}
          {/* Account (login) phone — read-only; this is the OTP number the vendor
              signed in with, distinct from the optional public business phone. */}
          <div data-testid="account-phone">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Account phone
            </p>
            <p className="mt-0.5 text-sm text-foreground">{p?.account_phone ?? '—'}</p>
          </div>
          <TextField
            label="Business phone"
            name="business_phone"
            optional
            value={phone}
            onChange={(e) => {
              set_phone(e.target.value);
              set_saved(false);
            }}
            data-testid="business-phone"
          />
          <TextField
            label="Instagram URL"
            name="instagram_url"
            optional
            placeholder="https://instagram.com/…"
            value={instagram}
            onChange={(e) => {
              set_instagram(e.target.value);
              set_saved(false);
            }}
            data-testid="instagram-url"
          />
          <TextField
            label="Facebook URL"
            name="facebook_url"
            optional
            placeholder="https://facebook.com/…"
            value={facebook}
            onChange={(e) => {
              set_facebook(e.target.value);
              set_saved(false);
            }}
            data-testid="facebook-url"
          />
          <Button
            type="submit"
            variant="gradient"
            className="w-fit"
            disabled={!dirty || update.isPending}
            data-testid="contact-socials-save"
          >
            {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
